import { useEffect, useRef, useState } from "react";
import { motion, ease as mmEase } from "@/lib/motion";
import { Upload, FileCheck2, ShieldCheck, Camera, IdCard, MapPin, BadgeCheck, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ez = mmEase.expo;

const DOC_STEPS = [
  { key: "passport",    label: "Passport",     icon: BadgeCheck, accent: "#7C3AED", desc: "International passport (preferred)" },
  { key: "national_id", label: "National ID",  icon: IdCard,     accent: "#06B6D4", desc: "Government-issued national ID" },
  { key: "selfie",      label: "Selfie",        icon: Camera,     accent: "#EF4444", desc: "Live photo with good lighting" },
  { key: "address",     label: "Address proof", icon: MapPin,     accent: "#10B981", desc: "Utility bill or bank statement (≤3 months old)" },
];

const STATUS_META = {
  unverified: { cls: "badge-navy",    label: "Not started", accent: "#475569" },
  pending:    { cls: "badge-gold",    label: "In review",   accent: "#F59E0B" },
  approved:   { cls: "badge-emerald", label: "Verified",    accent: "#10B981" },
  rejected:   { cls: "badge-red",     label: "Rejected",    accent: "#EF4444" },
};

export default function Kyc() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState({ kyc_status: "unverified", documents: [] });
  const inputRefs = useRef({});

  const load = () => api.get("/kyc/status").then(({ data }) => setStatus(data));
  useEffect(() => { load(); }, []);

  const uploaded      = new Set(status.documents.map((d) => d.doc_type));
  const meta          = STATUS_META[status.kyc_status] || STATUS_META.unverified;
  const uploadedCount = DOC_STEPS.filter((s) => uploaded.has(s.key)).length;

  const handleFile = async (doc_type, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("doc_type", doc_type);
    fd.append("file", file);
    try {
      await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${doc_type.replace("_", " ")} uploaded`);
      load(); refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">

      {/* ── Header ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,   filter: "blur(0px)" }}
        transition={{ duration: 0.45, ease: ez }}
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">Identity</p>
        <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight">KYC verification</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          A few documents stand between you and unlimited sending. Encrypted end-to-end.
        </p>
      </motion.div>

      {/* ── Status hero card ────────────────────────────────────── */}
      <motion.div
        className="card-elevated overflow-hidden"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.42, ease: ez, delay: 0.06 }}
      >
        <div className="brand-gradient px-6 pt-6 pb-5 relative overflow-hidden">
          <div
            className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-3xl pointer-events-none"
            style={{ background: "rgba(16,185,129,0.2)" }}
          />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/[0.12] border border-white/20 grid place-items-center text-white shrink-0">
                <ShieldCheck size={22} strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-bold">Identity status</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="font-display text-xl font-black text-white">{user?.full_name}</p>
                  <span className={meta.cls} data-testid="kyc-status-pill">{meta.label}</span>
                </div>
              </div>
            </div>
            {status.kyc_status === "approved" && (
              <div className="text-right hidden sm:block">
                <p className="text-white/40 text-[11px]">Monthly limit</p>
                <p className="font-display text-2xl font-black text-emerald num-tabular">$50,000</p>
              </div>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between text-[11px] mb-2">
            <span className="font-semibold text-navy">{uploadedCount} of {DOC_STEPS.length} documents uploaded</span>
            <span className="text-muted-foreground font-bold">{Math.round((uploadedCount / DOC_STEPS.length) * 100)}%</span>
          </div>
          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-emerald rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(uploadedCount / DOC_STEPS.length) * 100}%` }}
              transition={{ duration: 0.7, ease: ez }}
            />
          </div>
        </div>
      </motion.div>

      {/* ── Rejection notice ────────────────────────────────────── */}
      {status.kyc_status === "rejected" && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: ez }}
          className="card-elevated p-5 flex items-start gap-3 border-red-200 bg-red-50"
        >
          <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={17} />
          <div>
            <p className="font-bold text-red-700 text-sm">Documents need attention</p>
            <p className="text-sm text-red-600/80 mt-0.5">Please re-upload clearer copies of the documents below.</p>
          </div>
        </motion.div>
      )}

      {/* ── Document cards ──────────────────────────────────────── */}
      <div className="grid sm:grid-cols-2 gap-4">
        {DOC_STEPS.map((s, i) => {
          const has     = uploaded.has(s.key);
          const docInfo = status.documents.find((d) => d.doc_type === s.key);
          const Icon = s.icon;
          return (
            <motion.div
              key={s.key}
              data-testid={`kyc-card-${s.key}`}
              className={`card-float p-5 transition-all ${has ? "border-emerald/25" : ""}`}
              style={has ? { boxShadow: "var(--shadow-emerald-sm)" } : undefined}
              initial={{ opacity: 0, y: 12, scale: 0.96 }}
              animate={{ opacity: 1, y: 0,  scale: 1 }}
              transition={{ duration: 0.4, ease: ez, delay: 0.1 + i * 0.06 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-11 h-11 rounded-2xl grid place-items-center transition-all ${
                      has ? "bg-emerald text-white" : ""
                    }`}
                    style={!has ? { background: `${s.accent}14`, color: s.accent } : undefined}
                  >
                    <Icon size={18} strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-bold text-navy text-sm">{s.label}</p>
                    <p className="text-[11px] text-muted-foreground">{s.desc}</p>
                  </div>
                </div>
                {has && (
                  <span className="badge-emerald shrink-0">
                    <FileCheck2 size={9} /> Uploaded
                  </span>
                )}
                {docInfo?.status === "rejected" && (
                  <span className="shrink-0 text-[10px] font-bold text-red-500 bg-red-50 border border-red-100 px-2 py-0.5 rounded-full">
                    Rejected
                  </span>
                )}
              </div>

              <input
                ref={(el) => (inputRefs.current[s.key] = el)}
                type="file"
                accept={s.key === "selfie" ? "image/*" : ".jpg,.jpeg,.png,.pdf,image/*,application/pdf"}
                capture={s.key === "selfie" ? "user" : undefined}
                className="hidden"
                data-testid={`kyc-input-${s.key}`}
                onChange={(e) => handleFile(s.key, e.target.files?.[0])}
              />
              {docInfo?.status === "rejected" && (
                <p className="text-xs text-red-600 font-medium mb-2">
                  {docInfo?.rejection_reason || "Please resubmit a clearer copy of this document."}
                </p>
              )}
              <button
                data-testid={`kyc-upload-${s.key}`}
                onClick={() => inputRefs.current[s.key]?.click()}
                className={`w-full flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-xl transition-all ${
                  has
                    ? "bg-secondary/60 hover:bg-secondary text-muted-foreground hover:text-navy border border-border/60"
                    : "btn-navy justify-center"
                }`}
              >
                <Upload size={13} strokeWidth={2} />
                {has ? "Replace document" : "Upload"}
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* ── All-uploaded confirmation ───────────────────────────── */}
      {uploadedCount === DOC_STEPS.length && status.kyc_status !== "approved" && (
        <motion.div
          data-testid="kyc-submitted-notice"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: ez }}
          className="card-elevated p-5 flex items-start gap-3"
          style={{ border: "1px solid rgba(16,185,129,0.2)", background: "rgba(16,185,129,0.04)" }}
        >
          <CheckCircle2 size={17} className="text-emerald shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <p className="font-bold text-navy text-sm">All documents submitted</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Our compliance team is reviewing your documents. Verification typically completes within 1–2 hours.
              We'll notify you by email as soon as it's done.
            </p>
          </div>
        </motion.div>
      )}

      {/* ── Security notice ─────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.38, ease: ez, delay: 0.3 }}
        className="card-elevated p-5 flex items-start gap-3 border-emerald/20 bg-emerald/[0.03]"
      >
        <CheckCircle2 size={16} className="text-emerald shrink-0 mt-0.5" strokeWidth={2} />
        <p className="text-sm text-navy/75">
          All documents are <strong className="text-navy">AES-256 encrypted at rest</strong> and reviewed by our compliance team. Most reviews complete in under 24 hours.
        </p>
      </motion.div>
    </div>
  );
}
